import React, { Suspense, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { WindowSidebarLeftBlank18 } from 'shared/assets/icons';
import { Favorites } from 'shared/components/connected/favorites';
import { NavigationTagsList } from 'shared/components/connected/navigation-tags-list';
import { SetupChecklist } from 'shared/components/connected/setup-checklist';
import { SideNavigationViewOptions } from 'shared/components/connected/side-navigation-view-options';
import { Colors, IconButton, Sizes } from 'shared/components/ui/icon-button';
import { SideNavigationPanel } from 'shared/components/ui/side-navigation-panel';
import { sideNavigationHoverClass } from 'shared/constants';
import { useOpenSearchDialog } from 'shared/contexts/search';
import { useElectron } from 'shared/hooks/use-is-mac';
import { useKeysDown } from 'shared/hooks/use-keys-down';
import { useUpcomingSelectedView } from 'shared/hooks/use-upcoming-selected-view';
import { inboxNavigationItem, mainNavigation } from 'shared/routes/navigation';
import { Paths } from 'shared/routes/paths';
import { UpcomingViewOptions } from 'shared/types/upcoming-view-options';

import { BottomBar } from './bottom-bar';
import { NavigationSection, SubItem } from './navigation-section';
import { ResizableContainer } from './resizable-container';
import * as Styled from './side-navigation.style';
import { UserButton } from './user-button';

export type SideNavigationProps = {
  open: boolean;
  mobileOpen: boolean;
  toggleOpen: () => void;
  toggleMobileOpen: () => void;
  onNavigationItem: () => void;
  activePath?: Paths;
};

export const SideNavigation: React.FC<SideNavigationProps> = ({
  open,
  mobileOpen,
  toggleOpen,
  toggleMobileOpen,
  onNavigationItem,
  activePath,
}) => {
  const { t } = useTranslation();
  const { isMac } = useElectron();
  const openSearchDialog = useOpenSearchDialog();
  const { view } = useUpcomingSelectedView();

  const viewOptionsAvailable = view === UpcomingViewOptions.Calendar;
  const onUpcomingCalendar =
    activePath === Paths.Upcoming && viewOptionsAvailable;

  const [viewOptionsOpen, setViewOptionsOpen] = useState(onUpcomingCalendar);
  const [wasOnUpcomingCalendar, setWasOnUpcomingCalendar] =
    useState(onUpcomingCalendar);
  const showViewOptions = viewOptionsAvailable && viewOptionsOpen;

  // Landing on the Upcoming calendar slides the panel in; leaving it restores the main menu.
  if (wasOnUpcomingCalendar !== onUpcomingCalendar) {
    setWasOnUpcomingCalendar(onUpcomingCalendar);
    setViewOptionsOpen(onUpcomingCalendar);
  }

  const openViewOptions = useCallback(() => setViewOptionsOpen(true), []);
  const closeViewOptions = useCallback(() => setViewOptionsOpen(false), []);

  useKeysDown(['Escape'], closeViewOptions);

  return (
    <>
      <Styled.SmallScreenBackdrop
        onClick={toggleMobileOpen}
        $open={mobileOpen}
      />

      <ResizableContainer open={open} smallScreenOpen={mobileOpen}>
        <Styled.ContentContainer $open={open} $smallScreenOpen={mobileOpen}>
          {isMac && (
            <Styled.AppNavBar>
              <IconButton
                className={sideNavigationHoverClass}
                icon={WindowSidebarLeftBlank18}
                onClick={toggleOpen}
                size={Sizes.Medium}
                color={Colors.Default}
                preventDrag
              />
            </Styled.AppNavBar>
          )}
          <Styled.Header $isMac={isMac}>
            <UserButton
              onCloseSideNavigation={isMac ? undefined : toggleOpen}
              onCloseMobileSideNavigation={toggleMobileOpen}
              onSearch={openSearchDialog}
            />
          </Styled.Header>

          <Styled.PanelTrack $showPanel={showViewOptions}>
            <Styled.Pane $active={!showViewOptions}>
              <Styled.List>
                <Styled.ListItem>
                  <SubItem
                    {...inboxNavigationItem}
                    activePath={activePath}
                    onClick={onNavigationItem}
                  />
                </Styled.ListItem>

                {mainNavigation.map((navigationItem) => (
                  <Styled.ListItem key={navigationItem.id}>
                    <NavigationSection
                      translationLabel={navigationItem.translationLabel}
                      subItems={navigationItem.subItems}
                      initialOpen
                      onNavigationItem={onNavigationItem}
                      activePath={activePath}
                      onOpenViewOptions={
                        viewOptionsAvailable ? openViewOptions : undefined
                      }
                    />
                  </Styled.ListItem>
                ))}

                <Favorites />

                <NavigationTagsList />
              </Styled.List>
            </Styled.Pane>

            <Styled.Pane $active={showViewOptions}>
              {showViewOptions && (
                <SideNavigationPanel
                  backLabel={t('general.menu.navigation.menu')}
                  onBack={closeViewOptions}
                >
                  <Suspense fallback={null}>
                    <SideNavigationViewOptions />
                  </Suspense>
                </SideNavigationPanel>
              )}
            </Styled.Pane>
          </Styled.PanelTrack>

          {open && !showViewOptions && (
            <Styled.SetupChecklistFooter>
              <SetupChecklist />
            </Styled.SetupChecklistFooter>
          )}

          <BottomBar activePath={activePath} />
        </Styled.ContentContainer>
      </ResizableContainer>
    </>
  );
};
